import { QueryClient, DefaultOptions } from "@tanstack/react-query"

// Configuración global de defaults para React Query
const queryConfig: DefaultOptions = {
  queries: {
    // Configuración de cache
    staleTime: 5 * 60 * 1000, // 5 minutos - datos frescos
    cacheTime: 10 * 60 * 1000, // 10 minutos - mantener en cache
    refetchOnWindowFocus: false, // No refetch automático al recuperar foco
    refetchOnMount: true, // Refetch al montar el componente
    refetchOnReconnect: true, // Refetch al reconectar

    // Configuración de retry
    retry: (failureCount, error: any) => {
      // No reintentar para errores 4xx (client errors)
      if (error?.response?.status >= 400 && error?.response?.status < 500) {
        return false
      }
      // Reintentar hasta 3 veces para otros errores
      return failureCount < 3
    },
    retryDelay: (attemptIndex) => {
      // Backoff exponencial con jitter
      const baseDelay = 1000 // 1 segundo base
      const delay = baseDelay * Math.pow(2, attemptIndex)
      const jitter = Math.random() * 0.3 * delay // 30% de jitter
      return Math.min(delay + jitter, 30000) // Máximo 30 segundos
    },

    // Configuración de red
    networkMode: "online",

    // Configuración de suspense (opcional)
    suspense: false,
  },
  mutations: {
    // Configuración de retry para mutations
    retry: (failureCount, error: any) => {
      // No reintentar mutations por defecto
      return false
    },
    // Configuración de red para mutations
    networkMode: "online",
  },
}

// Crear QueryClient con configuración optimizada
export const queryClient = new QueryClient({
  defaultOptions: queryConfig,
})

// Configuración específica para diferentes tipos de datos
export const queryKeys = {
  // Productos y catálogo
  products: ["products"] as const,
  productCategories: ["productCategories"] as const,
  cart: ["cart"] as const,

  // Usuario y perfil
  user: ["user"] as const,
  userProfile: ["userProfile"] as const,
  userProfilePhotos: ["userProfilePhotos"] as const,

  // Organización
  organization: ["organization"] as const,
  orgSettings: ["orgSettings"] as const,
  orgModules: ["orgModules"] as const,

  // Posts y newsfeed
  posts: ["posts"] as const,
  postComments: ["postComments"] as const,

  // Comunicaciones
  communications: ["communications"] as const,
  notifications: ["notifications"] as const,

  // Archivos
  files: ["files"] as const,
  organizationFiles: ["organizationFiles"] as const,
} as const

// Función para invalidar queries relacionadas
export const invalidateRelatedQueries = (
  queryClient: QueryClient,
  key: string
) => {
  queryClient.invalidateQueries({ queryKey: [key] })
}

// Función para prefetch queries críticas
export const prefetchCriticalQueries = async (queryClient: QueryClient) => {
  // Prefetch queries que se necesitan inmediatamente
  await Promise.allSettled([
    queryClient.prefetchQuery({
      queryKey: queryKeys.orgSettings,
      staleTime: 5 * 60 * 1000,
    }),
    queryClient.prefetchQuery({
      queryKey: queryKeys.orgModules,
      staleTime: 10 * 60 * 1000,
    }),
  ])
}
